import { stripCloneSuffix, isCloneDatasetName } from '@/features/datasets/utils/datasetCloneNames';

/**
 * FloatingProgressBar key for an AI generation row.
 * Include *datasetName* so two Generate with AI runs on the same task
 * (e.g. ``harm_ai`` then ``harm_ai2``) get separate cards.
 */
export function aiGenerationProgressKey(usecaseId, datasetName) {
    if (!usecaseId) return '';
    if (datasetName) return `ai_gen_${usecaseId}__${datasetName}`;
    return `ai_gen_${usecaseId}`;
}

export function isAiGenerationProgressKeyForUsecase(key, usecaseId) {
    if (!key || !usecaseId) return false;
    return key === `ai_gen_${usecaseId}` || key.startsWith(`ai_gen_${usecaseId}__`);
}

function isTerminalProgressEntry(p) {
    if (!p || p.isDownload) return true;
    if (p.isCompleted || p.isError) return true;
    if (p.isGeneration) return false;
    const progress = typeof p.progress === 'number' ? p.progress : 0;
    return progress >= 100;
}

function listAiGenerationProgressEntries(progressStatus, usecaseId) {
    if (!progressStatus || !usecaseId) return [];
    return Object.entries(progressStatus)
        .filter(([key, value]) => {
            if (!value?.isGeneration) return false;
            if (value.usecaseId && String(value.usecaseId) === String(usecaseId)) return true;
            return isAiGenerationProgressKeyForUsecase(key, usecaseId);
        })
        .map(([progressKey, value]) => ({ ...value, progressKey }));
}

/**
 * Active AI dataset generation rows from `progressStatus` (non-terminal).
 */
export function listActiveAiGenerationEntriesForUsecase(progressStatus, usecaseId) {
    return listAiGenerationProgressEntries(progressStatus, usecaseId).filter(
        (entry) => !isTerminalProgressEntry(entry)
    );
}

/**
 * Active AI dataset generation row from `progressStatus` (non-terminal).
 * When *datasetName* is set, return that dataset's row instead of the first.
 */
export function getActiveAiGenerationEntryForUsecase(progressStatus, usecaseId, datasetName) {
    const entries = listActiveAiGenerationEntriesForUsecase(progressStatus, usecaseId);
    if (!entries.length) return null;
    if (datasetName) {
        return entries.find((entry) => sameDatasetName(entry.datasetName, datasetName)) || null;
    }
    return entries[0];
}

export function isAiGenerationInFlightForUsecase(progressStatus, usecaseId) {
    return listActiveAiGenerationEntriesForUsecase(progressStatus, usecaseId).length > 0;
}

function sameDatasetName(a, b) {
    if (!a || !b) return false;
    return (
        a === b ||
        stripCloneSuffix(a) === stripCloneSuffix(b) ||
        stripCloneSuffix(a) === b ||
        a === stripCloneSuffix(b)
    );
}

export function fileMatchesAiGenerationDataset(file, datasetName) {
    if (!datasetName || !file) return false;
    const names = [file.name, ...(file.cloneNames || [])].filter(Boolean);
    return names.some((n) => sameDatasetName(n, datasetName));
}

/**
 * Names used to match `usecase.datasets` / benchmarks against in-flight work.
 * Includes raw `progressStatus` keys for uploads and `datasetName` for AI generation rows.
 */
export function collectInFlightProgressFileNames(progressStatus) {
    const names = new Set();
    Object.entries(progressStatus || {}).forEach(([key, p]) => {
        if (!p || p.isDownload) return;
        if (p.isCompleted || p.isError) return;
        const progress = typeof p.progress === 'number' ? p.progress : 0;
        if (!p.isGeneration && !p.isRetrievalAgent && progress >= 100) return;
        if (p.isGeneration || p.isRetrievalAgent) {
            if (p.datasetName) {
                names.add(p.datasetName);
                names.add(stripCloneSuffix(p.datasetName));
            }
        } else {
            names.add(key);
            if (isCloneDatasetName(key)) {
                names.add(stripCloneSuffix(key));
            }
        }
    });
    return names;
}

function matchingActiveGenerationEntry(file, progressStatus, usecaseId) {
    return listActiveAiGenerationEntriesForUsecase(progressStatus, usecaseId).find((entry) =>
        fileMatchesAiGenerationDataset(file, entry.datasetName)
    );
}

/**
 * Per-file tile status for Data Overview: upload/processing keyed by filename,
 * or AI generation keyed by `ai_gen_<usecaseId>__<datasetName>` when `datasetName` matches the tile.
 */
export function resolveFileTileProgressStatus(file, progressStatus) {
    if (!file || !progressStatus) return null;

    const keysToCheck = new Set();
    if (file.name) keysToCheck.add(file.name);
    (file.cloneNames || []).forEach((n) => {
        if (n) {
            keysToCheck.add(n);
            keysToCheck.add(stripCloneSuffix(n));
        }
    });
    for (const key of keysToCheck) {
        const entry = progressStatus[key];
        if (!entry || entry.isDownload || entry.isGeneration) continue;
        if (entry.isCompleted || entry.isError) continue;
        const progress = typeof entry.progress === 'number' ? entry.progress : 0;
        if (progress >= 100) continue;
        const isProcessing = entry.phase === 'processing' || entry.phase === 'label_generation';
        return {
            inProgress: true,
            label: isProcessing ? 'Processing…' : 'Uploading…',
            progress,
            isError: Boolean(entry.isError),
        };
    }

    if (file.kind === 'dataset') {
        for (const usecaseId of file.usecaseIds || []) {
            const entry = matchingActiveGenerationEntry(file, progressStatus, usecaseId);
            if (!entry) continue;
            const progress = typeof entry.progress === 'number' ? entry.progress : 0;
            return {
                inProgress: true,
                label: 'Processing…',
                progress,
                isError: Boolean(entry.isError),
            };
        }
    }

    return null;
}

export function fileShouldShowAiDatasetSparkle(file, progressStatus, origin, isAiGeneratedFn) {
    if (isAiGeneratedFn?.(origin)) return true;
    if (file?.kind !== 'dataset') return false;
    for (const usecaseId of file.usecaseIds || []) {
        if (matchingActiveGenerationEntry(file, progressStatus, usecaseId)) return true;
    }
    return false;
}
